import React from 'react';
import {
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Skeleton,
  Alert,
} from '@mui/material';
import {
  Campaign as AnnouncementIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface Announcement {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'general';
  author: string;
  createdAt: string;
  priority?: 'high' | 'medium' | 'low';
}

interface AnnouncementsWidgetProps {
  announcements?: Announcement[];
  loading?: boolean;
  onAnnouncementClick?: (announcementId: string) => void;
}

const getAnnouncementIcon = (type: string) => {
  switch (type) {
    case 'info':
      return <InfoIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'success':
      return <SuccessIcon />;
    default:
      return <AnnouncementIcon />;
  }
};

const getAnnouncementColor = (type: string) => {
  switch (type) {
    case 'info':
      return 'info.main';
    case 'warning':
      return 'warning.main';
    case 'success':
      return 'success.main';
    default:
      return 'primary.main';
  }
};

const AnnouncementsWidget: React.FC<AnnouncementsWidgetProps> = ({
  announcements = [],
  loading = false,
  onAnnouncementClick,
}) => {
  const { t } = useTranslation();
  const renderSkeleton = () => (
    <>
      {[1, 2, 3].map((i) => (
        <ListItem key={i} alignItems="flex-start">
          <ListItemAvatar>
            <Skeleton variant="circular" width={40} height={40} />
          </ListItemAvatar>
          <ListItemText
            primary={<Skeleton variant="text" width="80%" />}
            secondary={
              <>
                <Skeleton variant="text" width="100%" />
                <Skeleton variant="text" width="60%" />
              </>
            }
          />
        </ListItem>
      ))}
    </>
  );

  // Show only high priority announcements as alerts at the top
  const highPriorityAnnouncements = announcements.filter(a => a.priority === 'high');
  const regularAnnouncements = announcements.filter(a => a.priority !== 'high');

  return (
    <Paper elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="h6">{t('dashboard.announcements')}</Typography>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {loading ? (
          <List>{renderSkeleton()}</List>
        ) : announcements.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <AnnouncementIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.noAnnouncements')}
            </Typography>
          </Box>
        ) : (
          <>
            {highPriorityAnnouncements.map((announcement) => (
              <Alert
                key={announcement.id}
                severity={announcement.type === 'general' ? 'info' : announcement.type}
                sx={{ m: 2, cursor: onAnnouncementClick ? 'pointer' : 'default' }}
                onClick={() => onAnnouncementClick?.(announcement.id)}
              >
                <Typography variant="subtitle2">{announcement.title}</Typography>
                <Typography variant="body2">{announcement.message}</Typography>
              </Alert>
            ))}

            <List>
              {regularAnnouncements.map((announcement) => (
                <ListItem
                  key={announcement.id}
                  alignItems="flex-start"
                  disablePadding={!!onAnnouncementClick}
                >
                  {onAnnouncementClick ? (
                    <ListItemButton
                      onClick={() => onAnnouncementClick(announcement.id)}
                      sx={{ alignItems: 'flex-start' }}
                    >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: getAnnouncementColor(announcement.type) }}>
                      {getAnnouncementIcon(announcement.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2">
                          {announcement.title}
                        </Typography>
                        {announcement.priority === 'medium' && (
                          <Chip label={t('common.important')} size="small" color="warning" variant="outlined" />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                          sx={{ display: 'block', mb: 0.5 }}
                        >
                          {announcement.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {announcement.author} • {formatDistanceToNow(new Date(announcement.createdAt), { addSuffix: true })}
                        </Typography>
                      </>
                    }
                  />
                    </ListItemButton>
                  ) : (
                    <>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: getAnnouncementColor(announcement.type) }}>
                          {getAnnouncementIcon(announcement.type)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">
                              {announcement.title}
                            </Typography>
                            {announcement.priority === 'medium' && (
                              <Chip label={t('common.important')} size="small" color="warning" variant="outlined" />
                            )}
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.primary"
                              sx={{ display: 'block', mb: 0.5 }}
                            >
                              {announcement.message}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {announcement.author} • {formatDistanceToNow(new Date(announcement.createdAt), { addSuffix: true })}
                            </Typography>
                          </>
                        }
                      />
                    </>
                  )}
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default AnnouncementsWidget;
